import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';
import useGetMetric from 'hooks/useGetMetric';
import { numberFormatter, numberReFormatter } from 'utils/helpers';

export interface Props {
  costDelivered: string;
  annualExpenditure: string | number;
  expectedConsumption: number;
}

const EconomicFeasibilityResult: FC<Props> = ({
  costDelivered,
  annualExpenditure,
  expectedConsumption,
}) => {
  const { t } = useTranslation();
  const { ton } = useGetMetric();
  const { currencyBlue } = useCurrency();

  const annualSavings = useMemo(() => {
    if (!costDelivered || !expectedConsumption) {
      return 0;
    }

    const omyaSum = Number(costDelivered) * expectedConsumption;
    const reformattedAnnualExpenditure = numberReFormatter(annualExpenditure);

    return Number(reformattedAnnualExpenditure) - omyaSum;
  }, [costDelivered, expectedConsumption, annualExpenditure]);

  const omyaProductArr = useMemo(
    () => [
      {
        id: 1,
        title: `${t('expectedConsumption')}:`,
        value: expectedConsumption + ` ${ton}/` + t('year'),
        color: 'black',
      },
      {
        id: 2,
        title: `${t('productCost')}:`,
        value:
          numberFormatter(Number(costDelivered) * expectedConsumption) +
          ` ${currencyBlue}/` +
          t('year'),
        color: 'black',
      },
      {
        id: 3,
        title: `${t('savings')}:`,
        value: numberFormatter(annualSavings) + ` ${currencyBlue}/` + t('year'),
        color: annualSavings > 0 ? 'green' : 'orange',
      },
    ],
    [
      annualSavings, 
      costDelivered, 
      currencyBlue, 
      expectedConsumption,
      t, 
      ton,
    ],
  );

  return (
    <div className="flex justify-between items-center md:flex-col break-inside-avoid">
      <div className="w-full md:ml-0 md:mt-4 border border-primary bg-primitiveBlue p-8 md:py-6 md:px-4 rounded-2xl">
        <div>
          {omyaProductArr.map((omyaProduct) => (
            <div className="flex" key={omyaProduct.id}>
              <div className="text-secondaryText mb-2">
                {omyaProduct.title} &nbsp;
              </div>
              {omyaProduct.id === 3 ? (
                <>
                  {omyaProduct.color === 'orange' ? (
                    <div className={'font-medium text-orange'}>
                      {omyaProduct.value}
                    </div>
                  ) : (
                    <div className={'font-medium text-green'}>
                      {omyaProduct.value}
                    </div>
                  )}
                </>
              ) : (
                <div className={'font-medium text-black'}>
                  {omyaProduct.value}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EconomicFeasibilityResult;
